import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CardContent, Skeleton, Stack, Typography, Rating } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

// Feature key -> gate.* translation subkey (leaf keys must not contain dots).
const GATE_KEYS = {
  'booking.orders': 'bookingOrders',
  'booking.availability': 'bookingAvailability',
  'booking.payments': 'bookingPayments',
  analytics: 'analytics',
  reviews: 'reviews',
};

/**
 * Static SYNTHETIC preview per feature (design §3): never real components, never
 * live data — CSS blur still exposes content through the DOM and the
 * accessibility tree, so the preview is fake by construction and aria-hidden.
 */
function SyntheticPreview({ feature }) {
  const bars = [72, 48, 90, 56, 66, 80];
  return (
    <Stack spacing={2}>
      {feature === 'analytics' && (
        <>
          <Stack direction="row" spacing={2}>
            {[0, 1, 2, 3].map((i) => (
              <Card key={i} sx={{ flex: 1 }}>
                <CardContent>
                  <Skeleton variant="text" width="60%" animation={false} />
                  <Typography variant="h4">{[128, 25, 9, 4.8][i]}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 140 }}>
                {bars.map((h, i) => (
                  <Box key={i} sx={{ flex: 1, height: `${h}%`, bgcolor: 'primary.light', opacity: 0.6, borderRadius: 1 }} />
                ))}
              </Box>
            </CardContent>
          </Card>
        </>
      )}
      {feature === 'reviews' && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              {[5, 4, 5].map((stars, i) => (
                <Box key={i}>
                  <Rating value={stars} readOnly size="small" />
                  <Skeleton variant="text" width={`${80 - i * 15}%`} animation={false} />
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
      {feature === 'booking.orders' && (
        <Card>
          <CardContent>
            <Skeleton variant="text" width="30%" animation={false} />
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Skeleton variant="rounded" width={48} height={48} animation={false} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" animation={false} />
                    <Skeleton variant="text" width="40%" animation={false} />
                  </Box>
                  <Skeleton variant="rounded" width={80} height={32} animation={false} sx={{ bgcolor: 'primary.light', opacity: 0.4 }} />
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
      {(feature === 'booking.availability' || feature === 'booking.payments') && (
        <Card>
          <CardContent>
            <Skeleton variant="text" width="40%" animation={false} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mt: 2 }}>
              {Array.from({ length: 21 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={36} animation={false} sx={i % 3 === 0 ? { bgcolor: 'primary.light', opacity: 0.4 } : undefined} />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

/**
 * `onUpgradeClick` is optional: when omitted the card renders without the
 * upgrade CTA — used when the provider is already Pro and the feature is simply
 * not available yet (e.g. payments behind the platform kill-switch).
 */
export default function ProTeaser({ feature, onUpgradeClick }) {
  const { t } = useTranslation();
  const gateKey = GATE_KEYS[feature] || feature;

  return (
    <Box sx={{ position: 'relative', minHeight: 420 }}>
      <Box aria-hidden="true" sx={{ filter: 'blur(4px)', opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }}>
        <SyntheticPreview feature={feature} />
      </Box>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card sx={{ maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: 8 }}>
          <CardContent sx={{ p: 4 }}>
            <LockOutlinedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {t(`gate.${gateKey}.title`)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: onUpgradeClick ? 3 : 0 }}>
              {t(`gate.${gateKey}.body`)}
            </Typography>
            {onUpgradeClick && (
              <Button variant="contained" size="large" onClick={onUpgradeClick} fullWidth>
                {t('gate.upgradeCta')}
              </Button>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
